import { UntypedFormGroup } from "@angular/forms";
import { CONSTANT } from "src/app/constant";
import { CandidateRole } from "src/app/models/enum";
import { AssessmentConfigRes, OBAttempt } from "src/app/models/models";
import { getObCellControl, resolveV2ObRoleSlot } from "../../shared-v2/utils/observation.utils";

/** Shared with PDF maneuver breakdown conditional OB badge (`#F9AC51`). */
export const TS1_CONDITIONAL_OB_BADGE_COLOR = "#F9AC51";
export const TS1_CONDITIONAL_OB_BADGE_SRC = "/assets/conditional_ob_c_yellow.svg";
export const TS1_PREDEFINED_OB_BADGE_TOOLTIP = "Predefined observation";
export const TS1_CONDITIONAL_OB_BADGE_TOOLTIP = "Conditional observation";

type AssessmentConfigObservation = AssessmentConfigRes["observations"][number] & {
	observationType?: string;
};

type AssessmentConfigEntry = {
	maneuverType?: { code?: string };
	maneuver?: { code?: string };
};

export function resolveAssessmentConfigManeuverCode(cfg: AssessmentConfigEntry): string {
	return (cfg.maneuverType?.code ?? cfg.maneuver?.code ?? "").trim();
}

export function normalizeConfigCandidateRole(role: string | undefined | null): CandidateRole | null {
	if (!role) return null;
	const normalized = role.toString().trim().toLowerCase();
	if (normalized === CandidateRole.PF || normalized === "pf") return CandidateRole.PF;
	if (normalized === CandidateRole.PM || normalized === "pm") return CandidateRole.PM;
	return null;
}

export function normalizeObservationConfigCode(code: string | undefined | null): string {
	return (code ?? "").trim().toLowerCase();
}

/** Observation code from admin config (`code` or legacy `observationType`). */
export function resolveObservationConfigCode(
	obs: Pick<AssessmentConfigObservation, "code"> & { observationType?: string },
): string {
	return normalizeObservationConfigCode(obs.code ?? obs.observationType);
}

export function findAssessmentConfigEntry(
	assessmentConfig: AssessmentConfigRes[],
	maneuverCode: string,
): AssessmentConfigRes | undefined {
	const normalized = maneuverCode?.trim();
	if (!normalized) return undefined;

	const exact = assessmentConfig.find((c) => resolveAssessmentConfigManeuverCode(c) === normalized);
	if (exact) return exact;

	const lower = normalized.toLowerCase();
	return assessmentConfig.find((c) => resolveAssessmentConfigManeuverCode(c).toLowerCase() === lower);
}

/** Keys: `${observationCode}::${candidateRole}` e.g. `pro_7::pilot_flying`. */
export function buildPredefinedObKeySet(
	assessmentConfig: AssessmentConfigRes[],
	maneuverCode: string,
): Set<string> {
	const keys = new Set<string>();
	if (!maneuverCode) return keys;

	const cfg = findAssessmentConfigEntry(assessmentConfig, maneuverCode);
	if (!cfg) return keys;

	for (const obs of cfg.observations ?? []) {
		const attempt = Number(obs.attempt ?? 0);
		if (attempt <= 0) continue;

		const observationCode = resolveObservationConfigCode(obs);
		if (!observationCode) continue;

		const role = normalizeConfigCandidateRole(obs.candidateRole);
		if (role) {
			keys.add(`${observationCode}::${role}`);
			continue;
		}

		keys.add(`${observationCode}::${CandidateRole.PF}`);
		keys.add(`${observationCode}::${CandidateRole.PM}`);
	}

	return keys;
}

export function isPredefinedObKey(
	predefinedKeys: Set<string>,
	obLabel: string,
	role: CandidateRole,
): boolean {
	const formatted = obLabel.toLowerCase();
	const spaceIdx = formatted.indexOf(" ");
	const observationCode =
		spaceIdx >= 0 ? `${formatted.slice(0, spaceIdx)}_${formatted.slice(spaceIdx + 1)}` : formatted;
	return predefinedKeys.has(`${observationCode}::${role}`);
}

/** Keys: `${observationCode}::${candidateRole}` for admin-marked conditional OBs. */
export function buildConditionalObKeySet(
	assessmentConfig: AssessmentConfigRes[],
	maneuverCode: string,
): Set<string> {
	const keys = new Set<string>();
	if (!maneuverCode) return keys;

	const cfg = findAssessmentConfigEntry(assessmentConfig, maneuverCode);
	if (!cfg) return keys;

	for (const obs of cfg.observations ?? []) {
		if (obs.isConditional !== true) continue;

		const observationCode = resolveObservationConfigCode(obs);
		if (!observationCode) continue;

		const role = normalizeConfigCandidateRole(obs.candidateRole);
		if (role) {
			keys.add(`${observationCode}::${role}`);
			continue;
		}

		keys.add(`${observationCode}::${CandidateRole.PF}`);
		keys.add(`${observationCode}::${CandidateRole.PM}`);
	}

	return keys;
}

export function isConditionalObKey(
	conditionalKeys: Set<string>,
	obLabel: string,
	role: CandidateRole,
): boolean {
	return isPredefinedObKey(conditionalKeys, obLabel, role);
}

export function isPredefinedObAttempt(value: OBAttempt | null | undefined): boolean {
	if (!value) return false;
	if (value.preDefined === true) return true;
	return value.lastAction === CONSTANT.OB_LAST_ACTION.PRE_SELECTED;
}

export function applyPreDefinedFlagsFromConfig(
	form: UntypedFormGroup,
	assessmentConfig: AssessmentConfigRes[],
	maneuverCode: string,
): void {
	const cfg = findAssessmentConfigEntry(assessmentConfig, maneuverCode);
	if (!cfg) return;

	for (const obs of cfg.observations ?? []) {
		const attempt = Number(obs.attempt ?? 0);
		if (attempt <= 0) continue;

		const { cat, num } = parseConfigObservationCode(resolveObservationConfigCode(obs));
		if (!cat || num == null) continue;

		const roles = resolveConfigObservationRoles(obs.candidateRole);
		for (const role of roles) {
			const slot = resolveV2ObRoleSlot(role);
			if (!slot) continue;

			const ctrl = getObCellControl(form, cat, num, slot);
			if (!ctrl) continue;

			const current = ctrl.value as OBAttempt;
			if (isPredefinedObAttempt(current)) continue;

			ctrl.patchValue(
				{
					...current,
					preDefined: true,
					lastAction: current.lastAction === "-" ? "pre-selected" : current.lastAction,
				},
				{ emitEvent: false },
			);
		}
	}
}

function parseConfigObservationCode(code: string | undefined | null): { cat: string; num: number | null } {
	const raw = normalizeObservationConfigCode(code);
	const match = raw.match(/^([a-z]+)_(\d+)$/);
	if (!match) return { cat: "", num: null };
	return { cat: match[1].toUpperCase(), num: Number(match[2]) };
}

function resolveConfigObservationRoles(candidateRole: string | undefined | null): CandidateRole[] {
	const role = normalizeConfigCandidateRole(candidateRole);
	if (role) return [role];
	return [CandidateRole.PF, CandidateRole.PM];
}

export type Ts1ObPreset = {
	maneuverCode: string;
	observationCode: string;
	candidateRole?: CandidateRole | string | null;
	activeAttempt?: number;
	totalAttempt?: number;
};

/** Form-defined OBs used when an N/A seat never received an add/remove click. */
export function collectAssessmentConfigObPresets(
	assessmentConfig: AssessmentConfigRes[] | undefined | null,
): Ts1ObPreset[] {
	const presets: Ts1ObPreset[] = [];
	for (const cfg of assessmentConfig ?? []) {
		const maneuverCode = resolveAssessmentConfigManeuverCode(cfg);
		if (!maneuverCode) continue;
		for (const obs of cfg.observations ?? []) {
			const attempt = Number(obs.attempt ?? 0);
			if (attempt <= 0) continue;
			const observationCode = resolveObservationConfigCode(obs);
			if (!observationCode) continue;
			for (const candidateRole of resolveConfigObservationRoles(obs.candidateRole)) {
				presets.push({
					maneuverCode,
					observationCode,
					candidateRole,
					activeAttempt: attempt,
					totalAttempt: attempt,
				});
			}
		}
	}
	return presets;
}
